import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2 } from "lucide-react";

interface PlanConfig {
  id: string;
  name: string;
  price: string;
  period: string;
  features: string[];
  locked: string[];
  highlight: boolean;
  badge: string;
  sort_order: number;
}

interface PlanEditDialogProps {
  plan: PlanConfig;
  open: boolean;
  onClose: () => void;
  onSave: (plan: PlanConfig) => Promise<void>;
}

const PlanEditDialog = ({ plan, open, onClose, onSave }: PlanEditDialogProps) => {
  const [form, setForm] = useState<PlanConfig>({ ...plan, features: [...plan.features], locked: [...plan.locked] });
  const [saving, setSaving] = useState(false);

  const updateFeature = (index: number, value: string) => {
    const updated = [...form.features];
    updated[index] = value;
    setForm({ ...form, features: updated });
  };

  const addFeature = () => setForm({ ...form, features: [...form.features, ""] });

  const removeFeature = (index: number) => {
    setForm({ ...form, features: form.features.filter((_, i) => i !== index) });
  };

  const updateLocked = (index: number, value: string) => {
    const updated = [...form.locked];
    updated[index] = value;
    setForm({ ...form, locked: updated });
  };

  const addLocked = () => setForm({ ...form, locked: [...form.locked, ""] });

  const removeLocked = (index: number) => {
    setForm({ ...form, locked: form.locked.filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Plano: {plan.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Badge</Label>
              <Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Ex: Mais vendido" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Preço</Label>
              <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <Label>Período</Label>
              <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
            </div>
          </div>

          {/* Features */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Itens inclusos</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addFeature}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {form.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={f} onChange={(e) => updateFeature(i, e.target.value)} className="flex-1" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeFeature(i)} className="flex-shrink-0 text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Locked */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Itens bloqueados</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addLocked}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {form.locked.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={f} onChange={(e) => updateLocked(i, e.target.value)} className="flex-1" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeLocked(i)} className="flex-shrink-0 text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {form.locked.length === 0 && (
                <p className="text-xs text-muted-foreground italic">Nenhum item bloqueado</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</> : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlanEditDialog;
